import { useSyncExternalStore } from "react";
import {
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router";
import { session } from "@shared/api/adminApi.ts";
import { Logo } from "@shared/components/Logo.tsx";
import { Icon, type IconName } from "@shared/components/Icon.tsx";
import { ToastProvider } from "@shared/components/Toast.tsx";

export function useSessionToken() {
  return useSyncExternalStore(
    session.subscribe,
    () => session.token,
    () => null,
  );
}

/** Redirects to /login when there is no (unexpired) token. A 401 anywhere clears it. */
export function RequireAdmin() {
  const token = useSessionToken();
  const loc = useLocation();
  if (!token)
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <AdminLayout />;
}

const NAV: { to: string; label: string; icon: IconName; end?: boolean }[] = [
  { to: "/", label: "डॅशबोर्ड", icon: "grid", end: true },
  { to: "/candidates", label: "स्पर्धक", icon: "users" },
  { to: "/legal", label: "माहिती, नियम आणि अटी", icon: "file" },
];

function AdminLayout() {
  const navigate = useNavigate();
  return (
    <ToastProvider>
      <div className="admin">
        <header className="admin-header">
          <div className="admin-container admin-header__top">
            <div className="admin-brand">
              <Logo size={44} />
              <div>
                <strong>शिव-वक्ता स्पर्धा २०२६-२७</strong>
                <span>
                  प्रशासक पोर्टल · छत्रपती सेवा प्रतिष्ठान, नागपूर-पुणे
                </span>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                session.clear();
                navigate("/login", { replace: true });
              }}
            >
              <Icon name="logout" size={16} />
              <span>बाहेर पडा</span>
            </button>
          </div>
          <nav className="admin-nav" aria-label="प्रशासक मेनू">
            <ul className="admin-container admin-nav__list">
              {NAV.map((n) => (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) => (isActive ? "is-active" : "")}
                  >
                    <Icon name={n.icon} size={18} />
                    <span>{n.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </header>
        <main className="admin-main">
          <Outlet />
        </main>
      </div>
    </ToastProvider>
  );
}

/** Orange page-title strip, same visual language as the public site's banners. */
export function AdminBanner({
  title,
  sub,
  actions,
}: {
  title: string;
  sub?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="admin-banner">
      <div className="admin-container admin-banner__inner">
        <div>
          <h1>{title}</h1>
          {sub && <p>{sub}</p>}
        </div>
        {actions && <div className="admin-banner__actions">{actions}</div>}
      </div>
    </div>
  );
}
